// Everything the timeline can draw, reduced to one shape.
//
// The entities disagree about time: a Task has a planned span, an Event is an
// instant, a metric Point is a reading at a moment. The timeline does not care —
// it needs a title, a start, an end (equal to the start for an instant), and a
// colour. Resolving those differences belongs here, once, not in the views.

import { useMemo, useSyncExternalStore } from "react";
import type { TaskInput } from "@/types";
import {
  activityStore,
  eventStore,
  eventTypeStore,
  goalStore,
  metricStore,
  planStore,
  pointStore,
  scheduleStore,
  taskStore,
} from "@/services/client";
import { expandBlock } from "./rrule";
import type { Range } from "./timeScale";

/**
 * What a mark on the timeline is:
 * - `task` — a task's *planned* window (plannedStartAt → plannedEndAt);
 * - `actual` — the same task as it *really* ran (actualStartAt → actualEndAt);
 * - `schedule` — one occurrence of an activity's recurring time budget;
 * - `event` — a moment that happened;
 * - `reading` — a metric point.
 *
 * Planned and actual are deliberately separate items: the contract stores them as
 * separate fields, and the whole value of seeing both is the gap between them.
 */
export type ItemKind = "task" | "actual" | "schedule" | "event" | "reading";

export type TimelineItem = {
  id: string;
  kind: ItemKind;
  title: string;
  startMs: number;
  /** Equal to startMs for an instant (an event, a reading). */
  endMs: number;
  /** The activity's colour, when the item can be traced back to one. */
  colorHex: string | undefined;
  status?: TaskInput["status"];
};

const FALLBACK_EVENT_TITLE = "Event";
const SCHEDULE_TITLE = "Scheduled";

/**
 * Every dated item across the feed, oldest first.
 *
 * The window is a parameter because a Schedule has no dates of its own — it is a
 * recurrence rule, and its occurrences only exist relative to the window being
 * looked at.
 */
export function useTimelineItems(range: Range): TimelineItem[] {
  const activities = useSyncExternalStore(activityStore.subscribe, activityStore.getSnapshot);
  const goals = useSyncExternalStore(goalStore.subscribe, goalStore.getSnapshot);
  const plans = useSyncExternalStore(planStore.subscribe, planStore.getSnapshot);
  const tasks = useSyncExternalStore(taskStore.subscribe, taskStore.getSnapshot);
  const events = useSyncExternalStore(eventStore.subscribe, eventStore.getSnapshot);
  const metrics = useSyncExternalStore(metricStore.subscribe, metricStore.getSnapshot);
  const points = useSyncExternalStore(pointStore.subscribe, pointStore.getSnapshot);
  const schedules = useSyncExternalStore(scheduleStore.subscribe, scheduleStore.getSnapshot);
  const eventTypes = useSyncExternalStore(eventTypeStore.subscribe, eventTypeStore.getSnapshot);

  return useMemo(() => {
    const colorOf = new Map(activities.map((a) => [a.id, a.fields.colorHex]));
    const activityName = new Map(activities.map((a) => [a.id, a.fields.shortName]));

    // A task knows its plan, a plan its goal, a goal its activity — the colour is at
    // the end of that chain, never on the task itself.
    const activityOfGoal = new Map(goals.map((g) => [g.id, g.fields.activityId]));
    const activityOfPlan = new Map(
      plans.map((p) => [p.id, activityOfGoal.get(p.fields.ltgId)]),
    );
    const activityOfMetric = new Map(
      metrics.map((m) => [
        m.id,
        m.fields.activityId ?? (m.fields.ltgId ? activityOfGoal.get(m.fields.ltgId) : undefined),
      ]),
    );

    const items: TimelineItem[] = [];

    for (const task of tasks) {
      const { plannedStartAt, plannedEndAt, actualStartAt, actualEndAt } = task.fields;
      const { title, status, planId } = task.fields;
      const activityId = planId === undefined ? undefined : activityOfPlan.get(planId);
      const colorHex = activityId === undefined ? undefined : colorOf.get(activityId);

      const plannedStart = toMs(plannedStartAt);
      if (plannedStart !== null) {
        items.push({
          id: task.id,
          kind: "task",
          title,
          startMs: plannedStart,
          endMs: toMs(plannedEndAt) ?? plannedStart,
          colorHex,
          status,
        });
      }

      // What actually happened, as its own mark. An open task (started, not finished)
      // runs to now — that is the honest end of a span that has not ended.
      const actualStart = toMs(actualStartAt);
      if (actualStart !== null) {
        items.push({
          id: `${task.id}:actual`,
          kind: "actual",
          title,
          startMs: actualStart,
          endMs: toMs(actualEndAt) ?? Date.now(),
          colorHex,
          status,
        });
      }
    }

    // A schedule is a rule, not a date: its occurrences exist only inside a window.
    for (const schedule of schedules) {
      const colorHex = colorOf.get(schedule.fields.activityId);
      const name = activityName.get(schedule.fields.activityId) ?? SCHEDULE_TITLE;
      for (const [blockIndex, block] of schedule.fields.blocks.entries()) {
        for (const occurrence of expandBlock(block, range)) {
          items.push({
            id: `${schedule.id}:${blockIndex}:${occurrence.startMs}`,
            kind: "schedule",
            title: name,
            startMs: occurrence.startMs,
            endMs: occurrence.endMs,
            colorHex,
          });
        }
      }
    }

    // An event names itself through its EventType; without one, it can only say
    // that something happened.
    const eventTypeName = new Map(eventTypes.map((t) => [t.id, t.fields.name]));
    for (const event of events) {
      const at = toMs(event.fields.at);
      if (at === null) continue;
      const activityId = event.fields.activityId;
      const name = eventTypeName.get(event.fields.eventTypeId) ?? FALLBACK_EVENT_TITLE;
      items.push({
        id: event.id,
        kind: "event",
        title: event.fields.body ? `${name}: ${event.fields.body}` : name,
        startMs: at,
        endMs: at,
        colorHex: activityId === undefined ? undefined : colorOf.get(activityId),
      });
    }

    const metricName = new Map(metrics.map((m) => [m.id, m.fields]));
    for (const point of points) {
      const at = toMs(point.fields.at);
      const metric = metricName.get(point.fields.metricId);
      if (at === null || metric === undefined) continue;
      const activityId = activityOfMetric.get(point.fields.metricId);
      items.push({
        id: point.id,
        kind: "reading",
        title: `${metric.name}: ${point.fields.value} ${metric.unit}`,
        startMs: at,
        endMs: at,
        colorHex: activityId === undefined ? undefined : colorOf.get(activityId),
      });
    }

    return items.sort((a, b) => a.startMs - b.startMs);
  }, [activities, goals, plans, tasks, events, eventTypes, metrics, points, schedules, range]);
}

function toMs(iso: string | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : null;
}
